import React, {useEffect, useRef, useState} from 'react';
import {Modal, Spin, Tree} from "antd";
import {request} from "../../util/request";
import { GLOBAL_STANDARD_TASK_API } from "../../config/api";
import {ObjTest, treeUtil} from "jzfz-web-utils";
import { getExpandedKeys, } from '../../util/common';
import {File3Icon} from "../icon/Icon";
import CustomEmpty from "../customEmpty/CustomEmpty";
import {FormInstance} from "antd/lib/form/hooks/useForm";
import {ModuleEnum} from "../../config/constant";


const { getListFromTree } = treeUtil;


interface valuesProps {
    taskId: string; // 引用任务的Id
    taskName: string;   // 引用任务的名称
    taskStatus: number; // 引用任务的状态值
}


interface ReferTaskModalProps {
    moduleType?: ModuleEnum;    // 模块类型
    form?: FormInstance;
    defaultCheckedKeys?: string[];   // 默认选中key
    visible: boolean;
    formData: any;
    onSubmit: (values: valuesProps[]) => void;
    onCancel: () => void;
}


/**
 * 引用标准任务弹窗组件
 * @param props
 * @constructor
 */
const ReferTaskModal = (props: ReferTaskModalProps) => {
    const { moduleType, form, defaultCheckedKeys = [], formData, visible, onSubmit, onCancel } = props;
    // 目录树
    const [treeData, setTreeData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
    // 线性目录树缓存
    let treeDataCache = useRef<any[]>([]);


    useEffect(() => {
        if (visible) {
            if (formData) {
                const {checkedKeys} = formData;
                setCheckedKeys(checkedKeys);
                setExpandedKeys([]);
                getTree();
            }
        }
    }, [formData, visible])


    /**
     * 获取选中结点key
     * @param list
     * @param checkedKeys
     */
    const getCheckedKeys = (list: any[], checkedKeys: string[]) => {
        list.forEach(v => {
            if (ObjTest.isNotEmptyArray(v.children)) {
                getCheckedKeys(v.children, checkedKeys);
                let flag = v.children.every(child => checkedKeys.includes(child.key));
                if (flag) {
                    checkedKeys.push(v.key);
                }
            }
        })
    }


    /**
     * 获取学习资料目录树
     * @param params
     */
    const getTree = () => {
        setLoading(true);
        request('/task/connect/tree', {}, {method: 'get', baseURL: GLOBAL_STANDARD_TASK_API}).then((res: any) => {
            let data = res.data || [];
            setTreeData(loop(data));
            treeDataCache.current = getListFromTree(data);
            // 设置选中结点key、展开结点key
            let keys = treeDataCache.current.filter((v: any) => defaultCheckedKeys.includes(v.pkId)).map((v: any) => v.pkId);
            let checkedKeys = [...keys];
            let expandedKeys = [];
            getExpandedKeys(data, expandedKeys, checkedKeys, 'pkId');
            setCheckedKeys(checkedKeys);
            setExpandedKeys(expandedKeys);
        }).finally(() => {
            setLoading(false);
        });
    }


    /**
     * 勾选结点
     * @param info
     */
    const checkHandle = (info: any, e: any) => {
        const { checked } = info;
        const { checked: flag, node: {children = []} } = e;
        let newCheckedKeys = [...checked];
        let childrenKeys = getListFromTree(children).map((v: any) => v.key);
        if (flag) {
            newCheckedKeys = Array.from(new Set([...newCheckedKeys, ...childrenKeys]));
        }else {
            newCheckedKeys = newCheckedKeys.filter((v: any) => !childrenKeys.includes(v));
        }
        setCheckedKeys(newCheckedKeys);
    }


    /**
     * 展开树结点
     */
    const expandHandle = (expandedKeys: any[]) => {
        setExpandedKeys(expandedKeys);
    }


    /**
     * 提交表单
     */
    const submit = () => {
        // 只获取任务类型结点的pkid
        let taskIds = treeDataCache.current.filter((v: any) => checkedKeys.includes(v.pkId) && v.type === 2).map((v: any) => v.pkId);

        setLoading(true);
        request('/task/connect/infos/tree', {taskIds}, {baseURL: GLOBAL_STANDARD_TASK_API}).then((res: any) => {
            let values = (res.data || []).map((v: any) => {
                return {pkid: v.pkId, name: v.name, status: v.status};
            })
            onSubmit(values);
        }).finally(() => {
            setLoading(false);
        })
    }


    /**
     * 获取checkbox禁用状态
     * @param data
     */
    const getDisabled = (data: any): boolean => {
        const { type, pkId } = data;
        // 不能添加该标准任务本身
        if (moduleType === ModuleEnum.standardTask && form) {
            const { taskId } = form.getFieldsValue();
            return type !== 2 || taskId === pkId;
        }
        return type !== 2;
    }


    const loop = (list: any[]): any[] => list.map((v: any) => {
        return {
            title: (
                <span title={v.name}>{v.name}
                {v.taskCount > 0 ? <i className="c1" style={{fontStyle: "normal"}}>({v.taskCount})</i> : null}
                </span>
            ),
            key: v.pkId,
            dataRef: v,
            icon: v.type === 2 ? <File3Icon style={{color: '#1887F1'}} /> : null,
            disableCheckbox: getDisabled(v),
            children: ObjTest.isNotEmptyArray(v.children) ? loop(v.children) : [],
        }
    })


    return (
        <Modal
            forceRender={true}
            maskClosable={false}
            closable={false}
            title="引用标准任务"
            visible={visible}
            confirmLoading={loading}
            onOk={submit}
            onCancel={onCancel}
            okButtonProps={{className: 'button-red'}}
        >
            <div style={{maxHeight: 600, overflow: "auto"}}>
                <Spin spinning={loading}>
                    {
                        ObjTest.isNotEmptyArray(treeData) ? (
                            <Tree
                                height={600}
                                checkable
                                showIcon={true}
                                selectable={false}
                                checkStrictly={true}
                                checkedKeys={checkedKeys}
                                expandedKeys={expandedKeys}
                                treeData={treeData}
                                onCheck={checkHandle}
                                onExpand={expandHandle}
                            />
                        ) : <CustomEmpty loading={loading} />
                    }
                </Spin>
            </div>
        </Modal>
    )
}


export default ReferTaskModal;
